import { QueryDocumentSnapshot, SnapshotOptions } from "firebase/firestore";

/**
 * Central, shared notification credentials, stored at
 * config/notificationCredentials and readable/writable ONLY by the superadmin.
 *
 * These are the technical settings (SMTP login, WhatsApp Cloud API, message
 * template) shared by every Gemeinde. The two secrets – emailSmtpPassword and
 * whatsappAccessToken – are write-only: the form sends a new value to the
 * saveNotificationSecrets callable, which stores it encrypted where no client
 * can read it. They are never loaded from or written to this document; the
 * has* flags only say whether one is set. Whether a Gemeinde may use a channel is
 * controlled per Gemeinde (Gemeinde.channels); the per-Gemeinde behaviour flags
 * live in GemeindeNotificationSettings.
 */
export default class NotificationCredentials {
    emailSmtpHost: string;
    emailSmtpPort: number;
    emailSmtpUser: string;
    emailSmtpPassword: string;
    emailFrom: string;
    whatsappAccessToken: string;
    whatsappPhoneNumberId: string;
    whatsappBusinessAccountId: string;
    whatsappApiVersion: string;
    whatsappTemplateName: string;
    whatsappTemplateLanguage: string;
    // Optional separate template for the choir conductor; empty = preacher template.
    whatsappChoirTemplateName: string;
    // Read-only state from the document, not part of the constructor.
    hasEmailSmtpPassword = false;
    hasWhatsappAccessToken = false;
    // True while a secret is still stored in plain text (before migrateSecrets ran).
    hasPlaintextSecrets = false;

    constructor(
        emailSmtpHost: string = "smtp.gmail.com",
        emailSmtpPort: number = 587,
        emailSmtpUser: string = "",
        emailSmtpPassword: string = "",
        emailFrom: string = "",
        whatsappAccessToken: string = "",
        whatsappPhoneNumberId: string = "",
        whatsappBusinessAccountId: string = "",
        whatsappApiVersion: string = "v21.0",
        whatsappTemplateName: string = "",
        whatsappTemplateLanguage: string = "de",
        whatsappChoirTemplateName: string = ""
    ) {
        this.emailSmtpHost = emailSmtpHost;
        this.emailSmtpPort = emailSmtpPort;
        this.emailSmtpUser = emailSmtpUser;
        this.emailSmtpPassword = emailSmtpPassword;
        this.emailFrom = emailFrom;
        this.whatsappAccessToken = whatsappAccessToken;
        this.whatsappPhoneNumberId = whatsappPhoneNumberId;
        this.whatsappBusinessAccountId = whatsappBusinessAccountId;
        this.whatsappApiVersion = whatsappApiVersion;
        this.whatsappTemplateName = whatsappTemplateName;
        this.whatsappTemplateLanguage = whatsappTemplateLanguage;
        this.whatsappChoirTemplateName = whatsappChoirTemplateName;
    }
}

export const notificationCredentialsConverter = {
    toFirestore: (c: NotificationCredentials) => {
        return {
            emailSmtpHost: c.emailSmtpHost,
            emailSmtpPort: c.emailSmtpPort,
            emailSmtpUser: c.emailSmtpUser,
            emailFrom: c.emailFrom,
            whatsappPhoneNumberId: c.whatsappPhoneNumberId,
            whatsappBusinessAccountId: c.whatsappBusinessAccountId,
            whatsappApiVersion: c.whatsappApiVersion,
            whatsappTemplateName: c.whatsappTemplateName,
            whatsappTemplateLanguage: c.whatsappTemplateLanguage,
            whatsappChoirTemplateName: c.whatsappChoirTemplateName,
        };
    },

    fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {
        const data = snapshot.data(options);
        // The secret inputs always start empty, even if a legacy plain-text value exists.
        const credentials = new NotificationCredentials(
            data.emailSmtpHost || "smtp.gmail.com",
            data.emailSmtpPort || 587,
            data.emailSmtpUser || "",
            "",
            data.emailFrom || "",
            "",
            data.whatsappPhoneNumberId || "",
            data.whatsappBusinessAccountId || "",
            data.whatsappApiVersion || "v21.0",
            data.whatsappTemplateName || "",
            data.whatsappTemplateLanguage || "de",
            data.whatsappChoirTemplateName || ""
        );
        credentials.hasEmailSmtpPassword = !!data.hasEmailSmtpPassword || !!data.emailSmtpPassword;
        credentials.hasWhatsappAccessToken = !!data.hasWhatsappAccessToken || !!data.whatsappAccessToken;
        credentials.hasPlaintextSecrets = !!data.emailSmtpPassword || !!data.whatsappAccessToken;
        return credentials;
    },
};
